<template>
  <div class="dashboard-wrapper">
    <div class="content-area">
      <div class="page-header">
        <h1 class="page-title">
          Hello!
        </h1>
        <div class="filter-bar">
          <button
            v-for="preset in presets"
            :key="preset.key"
            class="preset-btn"
            :class="{ active: selectedPreset === preset.key }"
            @click="selectPreset(preset.key)">
            {{ preset.label }}
          </button>
          <template v-if="selectedPreset === 'custom'">
            <div class="custom-range">
              <v-menu
                v-model="customStartMenu"
                :close-on-content-click="false"
                offset-y
                min-width="auto"
                transition="scale-transition">
                <template #activator="{ on, attrs }">
                  <button
                    class="date-btn"
                    v-bind="attrs"
                    v-on="on">
                    <v-icon size="14">
                      mdi-calendar
                    </v-icon>
                    {{ customStart || 'Başlangıç' }}
                  </button>
                </template>
                <v-date-picker
                  v-model="customStart"
                  no-title
                  :max="customEnd || undefined"
                  @input="customStartMenu = false"/>
              </v-menu>
              <span class="range-sep">→</span>
              <v-menu
                v-model="customEndMenu"
                :close-on-content-click="false"
                offset-y
                min-width="auto"
                transition="scale-transition">
                <template #activator="{ on, attrs }">
                  <button
                    class="date-btn"
                    v-bind="attrs"
                    v-on="on">
                    <v-icon size="14">
                      mdi-calendar
                    </v-icon>
                    {{ customEnd || 'Bitiş' }}
                  </button>
                </template>
                <v-date-picker
                  v-model="customEnd"
                  no-title
                  :min="customStart || undefined"
                  @input="customEndMenu = false"/>
              </v-menu>
              <button
                class="apply-btn"
                :disabled="!customStart || !customEnd"
                @click="applyCustomRange">
                Uygula
              </button>
            </div>
          </template>
        </div>
      </div>

      <div class="main-content">
        <div v-if="loading" class="loading-state">
          <v-progress-circular indeterminate color="primary" size="48"/>
        </div>

        <template v-else>
          <!-- 1. Resource Summary -->
          <div class="data-cards-row">
            <v-card
              v-for="item in resourceCards"
              :key="item.key"
              class="data-card">
              <div class="card-header">
                <span class="card-title">{{ item.label }}</span>
                <v-icon size="16" color="grey">
                  {{ item.icon }}
                </v-icon>
              </div>
              <div class="card-data">
                <span class="data-value">{{ item.value }}</span>
              </div>
            </v-card>
          </div>

          <!-- 2. Calls Over Time -->
          <v-card class="module-card">
            <div class="module-header">
              <h3 class="module-title">
                Calls Over Time
              </h3>
            </div>
            <div v-if="dashboardData.calls_over_time.length" class="chart-container">
              <apexchart
                type="bar"
                height="220"
                :options="callsOverTimeOptions"
                :series="callsOverTimeSeries"/>
            </div>
            <div v-else class="empty-state">
              <v-icon size="40" color="grey-lighten-1">mdi-chart-bar</v-icon>
              <p>No data for the selected period.</p>
            </div>
          </v-card>

          <!-- 3. Sentiment | Latency | Call Metrics -->
          <div class="three-col-row">
            <v-card class="module-card sentiment-module">
              <div class="module-header">
                <h3 class="module-title">
                  Sentiment
                </h3>
                <span class="module-badge">{{ dashboardData.sentiment_distribution.total }} calls</span>
              </div>
              <div class="sentiment-body">
                <div class="sentiment-item positive">
                  <span class="sentiment-pct">{{ dashboardData.sentiment_distribution.positive }}%</span>
                  <span class="sentiment-label">Positive</span>
                </div>
                <div class="sentiment-divider"/>
                <div class="sentiment-item neutral">
                  <span class="sentiment-pct">{{ dashboardData.sentiment_distribution.neutral }}%</span>
                  <span class="sentiment-label">Neutral</span>
                </div>
                <div class="sentiment-divider"/>
                <div class="sentiment-item negative">
                  <span class="sentiment-pct">{{ dashboardData.sentiment_distribution.negative }}%</span>
                  <span class="sentiment-label">Negative</span>
                </div>
              </div>
            </v-card>

            <v-card class="module-card latency-module">
              <div class="module-header">
                <h3 class="module-title">
                  Latency
                </h3>
              </div>
              <div class="latency-body">
                <div
                  v-for="item in latencyCards"
                  :key="item.key"
                  class="latency-item">
                  <span class="latency-value">{{ item.value }}</span>
                  <span class="latency-label">{{ item.label }}</span>
                </div>
              </div>
            </v-card>

            <v-card class="module-card metrics-module">
              <div class="module-header">
                <h3 class="module-title">
                  Call Metrics
                </h3>
              </div>
              <div class="metrics-body">
                <div class="metric-item">
                  <span class="metric-value">{{ avgDuration }}</span>
                  <span class="metric-label">Avg Duration</span>
                </div>
                <div class="metric-divider"/>
                <div class="metric-item">
                  <span class="metric-value">{{ dashboardData.mt_transferred_rate }}%</span>
                  <span class="metric-label">Transfer Rate</span>
                </div>
              </div>
            </v-card>
          </div>

          <!-- 4. Calls by Result Code -->
          <v-card class="module-card result-code-module">
            <div class="module-header">
              <h3 class="module-title">
                Calls by Result Code
              </h3>
              <button
                v-if="selectedResultCode !== null"
                class="module-badge clear-filter-badge"
                @click="clearResultCodeFilter">
                Code {{ selectedResultCode }}
                <v-icon size="12" color="grey">mdi-close</v-icon>
              </button>
            </div>
            <div
              v-if="dashboardData.calls_by_result_code.length"
              class="result-code-list">
              <div
                v-for="(item, i) in dashboardData.calls_by_result_code"
                :key="item.result_code"
                class="result-code-item"
                :class="{ active: selectedResultCode === item.result_code }"
                @click="selectResultCode(item.result_code)">
                <div class="rc-meta">
                  <span
                    class="rc-dot"
                    :style="{ background: CHART_COLORS[i % CHART_COLORS.length] }"/>
                  <span class="rc-code">Code {{ item.result_code }}</span>
                  <span class="rc-count">{{ item.count }} calls</span>
                  <span class="rc-percent">{{ item.percent }}%</span>
                </div>
                <div class="rc-bar-track">
                  <div
                    class="rc-bar-fill"
                    :style="{ width: `${item.percent}%`, background: CHART_COLORS[i % CHART_COLORS.length] }"/>
                </div>
              </div>
            </div>
            <div
              v-else
              class="empty-state">
              <v-icon size="40" color="grey-lighten-1">mdi-pound</v-icon>
              <p>No result code data yet.</p>
            </div>
          </v-card>

          <!-- 5. Calls by Agent + Top Topics -->
          <div class="two-col-row">
            <v-card class="module-card chart-module">
              <div class="module-header">
                <h3 class="module-title">
                  Calls by Agent
                </h3>
              </div>
              <div
                v-if="dashboardData.calls_by_agent.length"
                class="chart-container">
                <apexchart
                  type="donut"
                  height="260"
                  :options="chartOptions"
                  :series="chartSeries"/>
              </div>
              <div
                v-else
                class="empty-state">
                <v-icon size="40" color="grey-lighten-1">
                  mdi-chart-pie
                </v-icon>
                <p>No call data yet.</p>
              </div>
              <div
                v-if="durationByAgent.length"
                class="agent-duration-table">
                <div class="adt-header">
                  <span>Agent</span>
                  <span>Avg Duration</span>
                </div>
                <div
                  v-for="row in durationByAgent"
                  :key="row.agent_id"
                  class="adt-row">
                  <span class="adt-name">{{ row.name }}</span>
                  <span class="adt-duration">{{ row.duration }}</span>
                </div>
              </div>
            </v-card>

            <v-card class="module-card topics-module">
              <div class="module-header">
                <h3 class="module-title">
                  Top Topics
                </h3>
              </div>
              <div
                v-if="dashboardData.top_topics.length"
                class="topics-list">
                <div
                  v-for="(topic, i) in dashboardData.top_topics"
                  :key="i"
                  class="topic-item">
                  <div class="topic-meta">
                    <span class="topic-rank">#{{ i + 1 }}</span>
                    <span class="topic-name">{{ topic.topic }}</span>
                    <span class="topic-count">{{ topic.count }}</span>
                  </div>
                  <div class="topic-bar-track">
                    <div
                      class="topic-bar-fill"
                      :style="{ width: topicBarWidth(topic.count) }"/>
                  </div>
                </div>
              </div>
              <div
                v-else
                class="empty-state">
                <p>No topic data yet.</p>
              </div>
            </v-card>
          </div>

          <!-- 6. STT Usage + TTS Usage -->
          <div
            v-if="isSuperuser"
            class="two-col-row">
            <v-card class="module-card llm-module">
              <div class="module-header">
                <h3 class="module-title">
                  STT Usage
                </h3>
              </div>
              <div class="llm-grid">
                <div
                  v-for="item in sttCards"
                  :key="item.key"
                  class="llm-item">
                  <span class="llm-value">{{ item.value }}</span>
                  <span class="llm-label">{{ item.label }}</span>
                </div>
              </div>
            </v-card>

            <v-card class="module-card llm-module">
              <div class="module-header">
                <h3 class="module-title">
                  TTS Usage
                </h3>
              </div>
              <div class="llm-grid">
                <div
                  v-for="item in ttsCards"
                  :key="item.key"
                  class="llm-item">
                  <span class="llm-value">{{ item.value }}</span>
                  <span class="llm-label">{{ item.label }}</span>
                </div>
              </div>
            </v-card>
          </div>

          <!-- 7. TTS Usage by Model -->
          <v-card
            v-if="isSuperuser"
            class="module-card llm-model-module">
            <div class="module-header">
              <h3 class="module-title">
                TTS Usage by Model
              </h3>
            </div>
            <div
              v-if="dashboardData.tts_usage_by_model && dashboardData.tts_usage_by_model.length"
              class="tts-model-table">
              <div class="tmt-header">
                <span>Model</span>
                <span>Characters</span>
                <span>Audio Size</span>
                <span>Requests</span>
              </div>
              <div
                v-for="(m, i) in dashboardData.tts_usage_by_model"
                :key="m.model_id"
                class="tmt-row">
                <span class="tmt-name">{{ m.model_id ? m.model_id.slice(0, 8) : `Model ${i + 1}` }}</span>
                <span class="tmt-stat">{{ m.total_characters.toLocaleString() }}</span>
                <span class="tmt-stat">{{ formatBytes(m.total_audio_bytes) }}</span>
                <span class="tmt-stat">{{ m.request_count.toLocaleString() }}</span>
              </div>
            </div>
            <div
              v-else
              class="empty-state">
              <v-icon size="40" color="grey-lighten-1">mdi-text-to-speech</v-icon>
              <p>No TTS model data yet.</p>
            </div>
          </v-card>

          <!-- 8. LLM Usage Totals + By Model -->
          <div
            v-if="isSuperuser"
            class="two-col-row">
            <v-card class="module-card llm-module">
              <div class="module-header">
                <h3 class="module-title">
                  LLM Usage
                </h3>
              </div>
              <div class="llm-grid">
                <div
                  v-for="item in llmCards"
                  :key="item.key"
                  class="llm-item">
                  <span class="llm-value">{{ item.value }}</span>
                  <span class="llm-label">{{ item.label }}</span>
                </div>
              </div>
            </v-card>

            <v-card class="module-card llm-model-module">
              <div class="module-header">
                <h3 class="module-title">
                  Usage by Model
                </h3>
              </div>
              <div class="model-list">
                <div
                  v-for="m in dashboardData.llm_usage_by_model"
                  :key="m.model"
                  class="model-row">
                  <div class="model-top">
                    <span class="model-name">{{ m.model }}</span>
                    <div class="model-stats">
                      <span class="ms-stat">{{ formatTokens(m.total_tokens) }} tokens</span>
                      <span class="ms-dot">·</span>
                      <span class="ms-stat">{{ m.request_count.toLocaleString() }} requests</span>
                    </div>
                  </div>
                  <div class="model-bar">
                    <div
                      class="model-bar-prompt"
                      :style="{ flex: m.prompt_tokens }"
                      :title="`Prompt: ${formatTokens(m.prompt_tokens)}`"/>
                    <div
                      class="model-bar-completion"
                      :style="{ flex: m.completion_tokens }"
                      :title="`Completion: ${formatTokens(m.completion_tokens)}`"/>
                  </div>
                  <div class="model-bar-legend">
                    <span class="legend-prompt">Prompt {{ formatTokens(m.prompt_tokens) }}</span>
                    <span class="legend-completion">Completion {{ formatTokens(m.completion_tokens) }}</span>
                  </div>
                </div>
              </div>
            </v-card>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import DashboardService from '@/services/DashboardService';
import AgentService from '@/services/AgentService';

const CHART_COLORS = [
  '#4361EE', '#3ABCB8', '#F97316', '#A855F7',
  '#EF4444', '#22C55E', '#EAB308', '#EC4899',
];

function formatTokens(n) {
  if (!n) return '0';
  if (n >= 1000000) return `${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

function formatDuration(seconds) {
  if (seconds == null) return '-';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatMs(ms) {
  if (ms == null) return '-';
  return `${Math.round(ms)}ms`;
}

function formatBytes(bytes) {
  if (bytes == null || bytes === 0) return '0 B';
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

function formatRtf(rtf) {
  if (rtf == null) return '-';
  return `${(rtf * 100).toFixed(2)}%`;
}

function detectGranularity(items) {
  if (items.length < 2) return 'day';
  const diffDays = Math.abs(new Date(items[1].period) - new Date(items[0].period)) / 86400000;
  if (diffDays >= 25) return 'month';
  if (diffDays >= 6) return 'week';
  return 'day';
}

function formatPeriod(isoStr, granularity) {
  const d = new Date(isoStr);
  if (granularity === 'month') return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const EMPTY_DATA = {
  sentiment_distribution: { positive: 0, negative: 0, neutral: 0, total: 0 },
  calls_by_agent: [],
  calls_by_result_code: [],
  calls_over_time: [],
  duration_stats: { avg_seconds: null, by_agent: [] },
  mt_transferred_rate: 0,
  latency_stats: { avg_stt_ms: null, avg_ttft_ms: null, avg_itl_ms: null, avg_tts_ms: null, avg_tts_rtf: null },
  top_topics: [],
  resource_summary: { agents: 0, tts_models: 0, stt_models: 0, tools: 0, voices: 0, collections: 0 },
  llm_usage: { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0, request_count: 0 },
  llm_usage_by_model: [],
  stt_usage: { total_file_size_bytes: 0, total_requests: 0, avg_upstream_latency_ms: null },
  tts_usage: { total_characters: 0, total_audio_bytes: 0, total_requests: 0, avg_upstream_latency_ms: null },
  tts_usage_by_model: [],
};

export default {
  name: 'Dashboard',
  data() {
    return {
      loading: false,
      dashboardData: { ...EMPTY_DATA },
      agentNameMap: {},
      selectedPreset: '1m',
      customStart: null,
      customEnd: null,
      customStartMenu: false,
      customEndMenu: false,
      selectedResultCode: null,
      CHART_COLORS,
    };
  },
  computed: {
    ...mapGetters('auth', ['user', 'isSuperuser']),

    presets() {
      return [
        { key: '7d', label: 'Son 7 Gün' },
        { key: '1m', label: 'Son 1 Ay' },
        { key: '3m', label: 'Son 3 Ay' },
        { key: '12m', label: 'Son 12 Ay' },
        { key: 'custom', label: 'Custom' },
      ];
    },

    dateParams() {
      if (this.selectedPreset === 'custom') {
        if (!this.customStart || !this.customEnd) return {};
        return {
          start_date: Math.floor(new Date(`${this.customStart}T00:00:00`).getTime() / 1000),
          end_date: Math.floor(new Date(`${this.customEnd}T23:59:59`).getTime() / 1000),
        };
      }
      const now = new Date();
      const daysMap = { '7d': 7, '1m': 30, '3m': 90, '12m': 365 };
      const days = daysMap[this.selectedPreset] || 30;
      return {
        start_date: Math.floor((now.getTime() - days * 86400 * 1000) / 1000),
        end_date: Math.floor(now.getTime() / 1000),
      };
    },

    queryParams() {
      const params = { ...this.dateParams };
      if (this.selectedResultCode !== null) {
        params.result_code = this.selectedResultCode;
      }
      return params;
    },

    resourceCards() {
      const s = this.dashboardData.resource_summary;
      return [
        { key: 'agents', label: 'Agents', value: s.agents, icon: 'mdi-robot' },
        { key: 'tts_models', label: 'TTS Models', value: s.tts_models, icon: 'mdi-text-to-speech' },
      ];
    },

    callsGranularity() {
      return detectGranularity(this.dashboardData.calls_over_time);
    },

    callsOverTimeSeries() {
      return [{
        name: 'Calls',
        data: this.dashboardData.calls_over_time.map((item) => item.count),
      }];
    },

    callsOverTimeOptions() {
      const granularity = this.callsGranularity;
      const categories = this.dashboardData.calls_over_time.map(
        (item) => formatPeriod(item.period, granularity),
      );
      return {
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
        xaxis: {
          categories,
          labels: { style: { fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '12px', colors: '#5C6A75' } },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          labels: {
            style: { fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '12px', colors: '#5C6A75' },
            formatter: (v) => Math.round(v),
          },
        },
        colors: ['#4361EE'],
        dataLabels: { enabled: false },
        grid: { borderColor: '#E9EDF1', strokeDashArray: 4 },
        tooltip: {
          y: { formatter: (v) => `${v} calls` },
          style: { fontFamily: 'Plus Jakarta Sans, sans-serif' },
        },
      };
    },

    latencyCards() {
      const l = this.dashboardData.latency_stats;
      return [
        { key: 'stt', label: 'Avg STT', value: formatMs(l.avg_stt_ms) },
        { key: 'ttft', label: 'Avg TTFT', value: formatMs(l.avg_ttft_ms) },
        { key: 'itl', label: 'Avg ITL', value: formatMs(l.avg_itl_ms) },
        { key: 'tts', label: 'Avg TTS', value: formatMs(l.avg_tts_ms) },
        { key: 'tts_rtf', label: 'Avg TTS RTF', value: formatRtf(l.avg_tts_rtf) },
      ];
    },

    avgDuration() {
      return formatDuration(this.dashboardData.duration_stats.avg_seconds);
    },

    durationByAgent() {
      return (this.dashboardData.duration_stats.by_agent || []).map((item) => ({
        agent_id: item.agent_id,
        name: this.agentNameMap[item.agent_id] || item.agent_id,
        duration: formatDuration(item.avg_seconds),
      }));
    },

    chartSeries() {
      return this.dashboardData.calls_by_agent.map((a) => a.count);
    },

    chartOptions() {
      const labels = this.dashboardData.calls_by_agent.map(
        (a) => this.agentNameMap[a.agent_id] || a.agent_id,
      );
      return {
        labels,
        colors: CHART_COLORS,
        legend: {
          position: 'bottom',
          fontFamily: 'Plus Jakarta Sans, sans-serif',
          fontSize: '13px',
        },
        dataLabels: { style: { fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '12px' } },
        tooltip: {
          y: {
            formatter: (val, { seriesIndex }) => {
              const item = this.dashboardData.calls_by_agent[seriesIndex];
              return `${val} calls (${item.percent}%)`;
            },
          },
        },
        plotOptions: { pie: { donut: { size: '60%' } } },
      };
    },

    topicsMax() {
      const topics = this.dashboardData.top_topics;
      return topics.length ? topics[0].count : 1;
    },

    llmCards() {
      const u = this.dashboardData.llm_usage;
      return [
        { key: 'total', label: 'Total Tokens', value: formatTokens(u.total_tokens) },
        { key: 'prompt', label: 'Prompt Tokens', value: formatTokens(u.prompt_tokens) },
        { key: 'completion', label: 'Completion Tokens', value: formatTokens(u.completion_tokens) },
        { key: 'requests', label: 'Requests', value: u.request_count.toLocaleString() },
      ];
    },

    sttCards() {
      const u = this.dashboardData.stt_usage;
      return [
        { key: 'requests', label: 'Requests', value: u.total_requests.toLocaleString() },
        { key: 'file_size', label: 'Total File Size', value: formatBytes(u.total_file_size_bytes) },
        { key: 'latency', label: 'Avg Upstream Latency', value: formatMs(u.avg_upstream_latency_ms) },
      ];
    },

    ttsCards() {
      const u = this.dashboardData.tts_usage;
      return [
        { key: 'requests', label: 'Requests', value: u.total_requests.toLocaleString() },
        { key: 'characters', label: 'Total Characters', value: u.total_characters.toLocaleString() },
        { key: 'audio', label: 'Total Audio Size', value: formatBytes(u.total_audio_bytes) },
        { key: 'latency', label: 'Avg Upstream Latency', value: formatMs(u.avg_upstream_latency_ms) },
      ];
    },
  },
  async mounted() {
    await this.loadDashboardData();
  },
  methods: {
    formatTokens,
    formatBytes,

    selectPreset(key) {
      this.selectedPreset = key;
      if (key !== 'custom') this.loadDashboardData();
    },

    applyCustomRange() {
      if (this.customStart && this.customEnd) this.loadDashboardData();
    },

    topicBarWidth(count) {
      return `${Math.round((count / this.topicsMax) * 100)}%`;
    },

    selectResultCode(code) {
      if (this.selectedResultCode === code) return;
      this.selectedResultCode = code;
      this.loadDashboardData();
    },

    clearResultCodeFilter() {
      if (this.selectedResultCode === null) return;
      this.selectedResultCode = null;
      this.loadDashboardData();
    },

    async loadDashboardData() {
      this.loading = true;
      try {
        const [data, agentsResponse] = await Promise.all([
          DashboardService.getData(this.queryParams),
          AgentService.getAllAgents(),
        ]);

        this.dashboardData = { ...EMPTY_DATA, ...data };

        const agents = agentsResponse?.data || agentsResponse || [];
        if (Array.isArray(agents)) {
          this.agentNameMap = agents.reduce((map, agent) => {
            if (agent.id && agent.name) map[agent.id] = agent.name;
            return map;
          }, {});
        }
      } catch (error) {
        console.error('Dashboard data loading error:', error);
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          message: 'Dashboard verileri yüklenirken hata oluştu.',
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/colors.scss';
@import '@/styles/variables.scss';

// ── Page Header ─────────────────────────────────────────────
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

// ── Filter Bar ───────────────────────────────────────────────
.filter-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.preset-btn {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: $caption-1-size;
  font-weight: 600;
  color: #5C6A75;
  background: #F7F8FB;
  border: 1px solid #D3DAE0;
  border-radius: 20px;
  padding: 5px 14px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  white-space: nowrap;

  &:hover {
    border-color: #93A0AF;
    color: #171D22;
  }

  &.active {
    background: #171D22;
    border-color: #171D22;
    color: #fff;
  }
}

.custom-range {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 4px;

  .range-sep {
    font-size: $caption-1-size;
    color: #93A0AF;
  }
}

.date-btn {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: $caption-1-size;
  font-weight: 500;
  color: #171D22;
  background: #fff;
  border: 1px solid #D3DAE0;
  border-radius: 8px;
  padding: 5px 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: border-color 0.15s;

  &:hover { border-color: #93A0AF; }
}

.apply-btn {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: $caption-1-size;
  font-weight: 600;
  color: #fff;
  background: #4361EE;
  border: none;
  border-radius: 8px;
  padding: 6px 14px;
  cursor: pointer;
  transition: opacity 0.15s;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:not(:disabled):hover { opacity: 0.85; }
}

.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
}

.main-content {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

// ── Resource Summary ────────────────────────────────────────
.data-cards-row {
  display: flex;
  gap: 12px;

  .data-card {
    flex: 1;
    background: #F7F8FB;
    border-radius: 12px;
    box-shadow: none;

    .card-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 12px;
      border-bottom: 1px solid #D3DAE0;
      height: 40px;

      .card-title {
        font-family: 'Plus Jakarta Sans', sans-serif;
        font-weight: 600;
        font-size: $body-3-size;
        color: #5C6A75;
        flex: 1;
      }
    }

    .card-data {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 12px;

      .data-value {
        font-family: 'Plus Jakarta Sans', sans-serif;
        font-weight: 700;
        font-size: $h5-size;
        color: #171D22;
      }
    }
  }
}

// ── Shared module card ──────────────────────────────────────
.module-card {
  border: 1px solid #D3DAE0;
  border-radius: 12px;
  box-shadow: none !important;

  .module-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 20px;
    border-bottom: 1px solid #D3DAE0;

    .module-title {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 700;
      font-size: $body-2-size;
      color: #171D22;
      margin: 0;
    }

    .module-badge {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 500;
      font-size: $caption-1-size;
      color: #5C6A75;
      background: #F7F8FB;
      border: 1px solid #D3DAE0;
      border-radius: 20px;
      padding: 2px 10px;
    }
  }
}

.chart-container {
  padding: 16px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 40px;
  color: #93A0AF;

  p {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: $body-3-size;
    margin: 0;
  }
}

// ── Row layouts ─────────────────────────────────────────────
.three-col-row,
.two-col-row {
  display: flex;
  gap: 16px;

  .module-card {
    flex: 1;
  }
}

// ── Sentiment ───────────────────────────────────────────────
.sentiment-module {
  .sentiment-body {
    display: flex;
    align-items: stretch;
    padding: 24px 20px;
  }

  .sentiment-divider {
    width: 1px;
    background: #D3DAE0;
    margin: 0 20px;
  }

  .sentiment-item {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;

    .sentiment-pct {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 700;
      font-size: $h4-size;
    }

    .sentiment-label {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 500;
      font-size: $caption-1-size;
      color: #5C6A75;
    }

    &.positive .sentiment-pct { color: #22C55E; }
    &.neutral  .sentiment-pct { color: #5C6A75; }
    &.negative .sentiment-pct { color: #EF4444; }
  }
}

// ── Latency ─────────────────────────────────────────────────
.latency-module {
  .latency-body {
    display: flex;
    align-items: stretch;
    padding: 24px 20px;
    gap: 0;

    .latency-item {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 6px;

      &:not(:last-child) {
        border-right: 1px solid #D3DAE0;
        margin-right: 20px;
        padding-right: 20px;
      }

      .latency-value {
        font-family: 'Plus Jakarta Sans', sans-serif;
        font-weight: 700;
        font-size: $h5-size;
        color: #171D22;
      }

      .latency-label {
        font-family: 'Plus Jakarta Sans', sans-serif;
        font-weight: 500;
        font-size: $caption-1-size;
        color: #5C6A75;
      }
    }
  }
}

// ── Call Metrics ─────────────────────────────────────────────
.metrics-module {
  .metrics-body {
    display: flex;
    align-items: stretch;
    padding: 24px 20px;

    .metric-divider {
      width: 1px;
      background: #D3DAE0;
      margin: 0 20px;
    }

    .metric-item {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 6px;

      .metric-value {
        font-family: 'Plus Jakarta Sans', sans-serif;
        font-weight: 700;
        font-size: $h5-size;
        color: #171D22;
      }

      .metric-label {
        font-family: 'Plus Jakarta Sans', sans-serif;
        font-weight: 500;
        font-size: $caption-1-size;
        color: #5C6A75;
      }
    }
  }
}

// ── Agent Duration Table ─────────────────────────────────────
.agent-duration-table {
  border-top: 1px solid #D3DAE0;
  padding: 12px 20px;

  .adt-header {
    display: flex;
    justify-content: space-between;
    padding: 0 0 8px 0;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 600;
    font-size: $caption-1-size;
    color: #93A0AF;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .adt-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-top: 1px solid #F0F2F5;

    .adt-name {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: $body-3-size;
      color: #171D22;
      font-weight: 500;
      word-break: break-all;
    }

    .adt-duration {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: $body-3-size;
      color: #5C6A75;
      font-weight: 600;
    }
  }
}

// ── Top Topics ───────────────────────────────────────────────
.topics-module {
  .topics-list {
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 14px;

    .topic-item {
      display: flex;
      flex-direction: column;
      gap: 6px;

      .topic-meta {
        display: flex;
        align-items: center;
        gap: 8px;

        .topic-rank {
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: $caption-1-size;
          font-weight: 600;
          color: #93A0AF;
          width: 24px;
          flex-shrink: 0;
        }

        .topic-name {
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: $body-3-size;
          font-weight: 500;
          color: #171D22;
          flex: 1;
        }

        .topic-count {
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: $body-3-size;
          font-weight: 700;
          color: #5C6A75;
        }
      }

      .topic-bar-track {
        height: 4px;
        background: #E9EDF1;
        border-radius: 2px;
        overflow: hidden;

        .topic-bar-fill {
          height: 100%;
          background: #4361EE;
          border-radius: 2px;
          transition: width 0.4s ease;
        }
      }
    }
  }
}

// ── Calls by Result Code ────────────────────────────────────
.clear-filter-badge {
  display: flex;
  align-items: center;
  gap: 2px;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;

  &:hover {
    border-color: #93A0AF;
    color: #171D22;
  }
}

.result-code-module {
  .result-code-list {
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .result-code-item {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 6px;
    margin: -6px;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.15s;

    &:hover { background: #F7F8FB; }

    &.active {
      background: #F0F3FF;

      .rc-code { color: #4361EE; }
    }

    .rc-meta {
      display: flex;
      align-items: center;
      gap: 8px;

      .rc-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        flex-shrink: 0;
      }

      .rc-code {
        font-family: 'Plus Jakarta Sans', sans-serif;
        font-size: $body-3-size;
        font-weight: 600;
        color: #171D22;
        flex: 1;
      }

      .rc-count {
        font-family: 'Plus Jakarta Sans', sans-serif;
        font-size: $caption-1-size;
        color: #5C6A75;
      }

      .rc-percent {
        font-family: 'Plus Jakarta Sans', sans-serif;
        font-size: $body-3-size;
        font-weight: 700;
        color: #5C6A75;
        min-width: 42px;
        text-align: right;
      }
    }

    .rc-bar-track {
      height: 4px;
      background: #E9EDF1;
      border-radius: 2px;
      overflow: hidden;

      .rc-bar-fill {
        height: 100%;
        border-radius: 2px;
        transition: width 0.4s ease;
      }
    }
  }
}

// ── LLM Usage ───────────────────────────────────────────────
.llm-module {
  .llm-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1px;
    background: #D3DAE0;
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
    overflow: hidden;

    .llm-item {
      background: #F7F8FB;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      padding: 20px 16px;

      .llm-value {
        font-family: 'Plus Jakarta Sans', sans-serif;
        font-weight: 700;
        font-size: $h5-size;
        color: #171D22;
      }

      .llm-label {
        font-family: 'Plus Jakarta Sans', sans-serif;
        font-weight: 500;
        font-size: $caption-1-size;
        color: #5C6A75;
      }
    }
  }
}

// ── LLM by Model ────────────────────────────────────────────
.llm-model-module {
  .model-list {
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 20px;

    .model-row {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .model-top {
        display: flex;
        flex-direction: column;
        gap: 2px;

        .model-name {
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-weight: 600;
          font-size: $body-3-size;
          color: #171D22;
        }

        .model-stats {
          display: flex;
          align-items: center;
          gap: 6px;

          .ms-stat {
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-size: $caption-1-size;
            color: #5C6A75;
          }

          .ms-dot {
            color: #D3DAE0;
            font-size: $caption-1-size;
          }
        }
      }

      .model-bar {
        display: flex;
        height: 6px;
        border-radius: 3px;
        overflow: hidden;
        gap: 2px;

        .model-bar-prompt {
          background: #4361EE;
          border-radius: 3px 0 0 3px;
        }

        .model-bar-completion {
          background: #3ABCB8;
          border-radius: 0 3px 3px 0;
        }
      }

      .model-bar-legend {
        display: flex;
        gap: 16px;

        .legend-prompt,
        .legend-completion {
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: $caption-1-size;
          color: #5C6A75;
          display: flex;
          align-items: center;
          gap: 4px;

          &::before {
            content: '';
            width: 8px;
            height: 8px;
            border-radius: 50%;
            display: inline-block;
          }
        }

        .legend-prompt::before    { background: #4361EE; }
        .legend-completion::before { background: #3ABCB8; }
      }
    }
  }
}

// ── TTS by Model Table ───────────────────────────────────────
.tts-model-table {
  padding: 12px 20px;

  .tmt-header,
  .tmt-row {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr 1fr;
    gap: 8px;
    align-items: center;
    padding: 8px 0;
  }

  .tmt-header {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 600;
    font-size: $caption-1-size;
    color: #93A0AF;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border-bottom: 1px solid #D3DAE0;
  }

  .tmt-row {
    border-top: 1px solid #F0F2F5;

    .tmt-name {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: $body-3-size;
      font-weight: 600;
      color: #171D22;
      font-variant-numeric: tabular-nums;
    }

    .tmt-stat {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: $body-3-size;
      color: #5C6A75;
      font-variant-numeric: tabular-nums;
    }
  }
}

// ── Responsive ───────────────────────────────────────────────
@media (max-width: 960px) {
  .data-cards-row {
    flex-wrap: wrap;

    .data-card { flex: 1 1 calc(33% - 8px); }
  }

  .three-col-row { flex-direction: column; }
  .two-col-row   { flex-direction: column; }
}

@media (max-width: 600px) {
  .data-cards-row {
    .data-card { flex: 1 1 calc(50% - 6px); }
  }
}
</style>
